import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { Filter, MessageSquare, Calendar, Download, Upload, ChevronDown, Copy } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOKRs, useDeleteOKR, useDuplicateOKR, useOKRFilters, usePermissions } from '../../hooks';
import { useOpenWithReturn } from '../../hooks/useReturnNavigation';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { EmptyState } from '../../components/common/EmptyState';
import { ExportModal } from '../../components/common/ExportModal';
import { ImportModal } from '../../components/common/ImportModal';
import { FilterPanel, SortControls, ActiveFilters } from '../../components/okr';
import { toast } from 'react-hot-toast';
import { getErrorMessage } from '../../services/api';
import type { OKR, KeyResult } from '../../types/okr';
import type { ImportType } from '../../services/import.service';
import { okrStatusLabel } from '../../utils/okrStatus';
import { isUuid, resolveIdSelection } from '../../utils/departmentSelection';

export const OKRList: React.FC = () => {
  const { user } = useAuth();
  const { canCreateOKR, canDeleteOKR } = usePermissions();
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importType, setImportType] = useState<ImportType>('objectives');
  const [showImportMenu, setShowImportMenu] = useState(false);

  // Use advanced filters hook
  const {
    filters,
    metadata,
    isMetadataError,
    updateFilters,
    removeFilter,
    clearFilters,
    hasActiveFilters,
  } = useOKRFilters();

  // Handle search from Header navigation (including same-page searches)
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const lastSearchTimestamp = useRef<number | null>(null);

  useEffect(() => {
    // Check if this is a new search from Header (via state.searchTimestamp)
    const state = location.state as { searchTimestamp?: number } | null;
    const searchTimestamp = state?.searchTimestamp;

    if (searchTimestamp && searchTimestamp !== lastSearchTimestamp.current) {
      lastSearchTimestamp.current = searchTimestamp;
      const searchFromUrl = searchParams.get('search');
      if (searchFromUrl) {
        updateFilters({ search: searchFromUrl });
      }
    }
  }, [location.state, searchParams, updateFilters]);

  // `filters.departmentIds` may still hold a legacy NAME from a pre-Department/Team-model
  // link (`?departmentIds=Sales`) rather than a `Department.id` uuid — the OKR list API
  // answers a name with 422. Resolve against filter metadata before ever sending the
  // request, the same way Reports resolves `?rdept=<name>` (ReportsPage.tsx). If the metadata
  // request FAILED, a name can never resolve and waiting would spin forever: keep only the
  // uuid-shaped values and run the list — the name is dropped, never sent.
  const departmentSelection = useMemo(() => {
    const raw = filters.departmentIds ?? [];
    return isMetadataError && !metadata
      ? resolveIdSelection(raw.filter(isUuid), undefined)
      : resolveIdSelection(raw, metadata?.departments);
  }, [filters.departmentIds, metadata, isMetadataError]);

  const okrQueryFilters = useMemo(
    () => ({ ...filters, departmentIds: departmentSelection.ids }),
    [filters, departmentSelection.ids]
  );

  const {
    data,
    isLoading: isLoadingOKRs,
    error,
    refetch,
  } = useOKRs(okrQueryFilters, { enabled: !departmentSelection.pending });

  // While a name is still unresolved, no request was even sent — show the list's existing
  // loading state rather than a misleading "No OKRs found" for an empty, disabled query.
  const isLoading = isLoadingOKRs || departmentSelection.pending;

  // Once metadata has loaded, write the resolved id(s) back to the URL (via `updateFilters`,
  // which syncs the URL with `replace`) so a shared/reloaded link is id-only from then on and
  // the name is never re-resolved on every render.
  useEffect(() => {
    if (!metadata) return;
    const raw = filters.departmentIds ?? [];
    const resolved = departmentSelection.ids;
    const alreadyResolved =
      raw.length === resolved.length && raw.every((value, index) => value === resolved[index]);
    if (alreadyResolved) return;
    updateFilters({ departmentIds: resolved });
  }, [metadata, filters.departmentIds, departmentSelection.ids, updateFilters]);

  // Open an OKR and tell its "← Back" where to return. The filters live in this page's URL, so
  // without it Back lands on a bare `/okrs` and every filter — the person picked in the
  // advanced filter, the cycle, the status — is gone.
  const openWithReturn = useOpenWithReturn();
  const openOKR = (okrId: string) => openWithReturn(`/okrs/${okrId}`);
  const deleteOKRMutation = useDeleteOKR();
  const duplicateOKRMutation = useDuplicateOKR();

  const handleDelete = async (okr: OKR) => {
    // Check if OKR has key results with progress
    const hasProgressKRs = okr.keyResults?.some((kr: KeyResult) =>
      kr.currentValue !== kr.startValue || (kr.progressUpdates && kr.progressUpdates.length > 0)
    );

    let confirmMessage = 'Are you sure you want to delete this OKR?';

    if (hasProgressKRs) {
      confirmMessage = `⚠️ WARNING: This OKR has Key Results with progress updates.\n\n` +
        `Deleting this OKR will permanently remove all progress data.\n\n` +
        `Are you sure you want to delete this OKR?`;
    }

    if (window.confirm(confirmMessage)) {
      try {
        await deleteOKRMutation.mutateAsync(okr.id);
        refetch();
        toast.success('OKR deleted successfully');
      } catch (error) {
        console.error('Delete error:', error);
        toast.error(getErrorMessage(error));
      }
    }
  };

  const handleDuplicate = async (okr: OKR) => {
    try {
      await duplicateOKRMutation.mutateAsync(okr.id);
      refetch();
      toast.success(`OKR duplicated successfully as "COPY - ${okr.title}"`);
    } catch (error) {
      console.error('Duplicate error:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const getStatusBadgeVariant = (status: string): 'success' | 'warning' | 'danger' | 'primary' | 'secondary' => {
    switch (status) {
      case 'on_track':
        return 'success';
      case 'at_risk':
        return 'warning';
      case 'off_track':
        return 'danger';
      case 'completed':
        return 'success';
      case 'closed':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getTypeBadgeVariant = (type: string): 'success' | 'warning' | 'danger' | 'primary' | 'secondary' => {
    switch (type) {
      case 'company':
        return 'primary';
      case 'department':
        return 'warning';
      case 'team':
        return 'success';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <div className="px-4 sm:px-6 lg:px-10 py-8">
        {/* Filter Controls Bar */}
        <Card className="p-4 mb-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            {/* Left Side: Filter Button and Active Filters */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <button
                onClick={() => setIsFilterPanelOpen(true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 font-medium transition-all ${
                  hasActiveFilters
                    ? 'bg-primary-50 border-primary-500 text-primary-700 shadow-sm'
                    : 'bg-white border-stone-200 text-stone-700 hover:bg-stone-50'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
                {hasActiveFilters && (
                  <span className="ml-1 px-2 py-0.5 bg-primary-500 text-white text-xs font-bold rounded-full">
                    Active
                  </span>
                )}
              </button>

              {/* Current Cycle toggle — standalone outside the filter panel */}
              {(() => {
                const activeCycle = metadata?.cycles?.find((c: any) => c.isActive);
                if (!activeCycle) return null;
                const isActive = filters.cycleIds?.includes(activeCycle.id) ?? false;
                return (
                  <button
                    onClick={() => {
                      if (isActive) {
                        // Remove cycle filter
                        updateFilters({ cycleIds: undefined });
                      } else {
                        updateFilters({ cycleIds: [activeCycle.id] });
                      }
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 font-medium text-sm transition-all ${
                      isActive
                        ? 'bg-primary-50 border-primary-500 text-primary-700 shadow-sm'
                        : 'bg-white border-stone-200 text-stone-700 hover:bg-stone-50'
                    }`}
                  >
                    <Calendar className="w-4 h-4" />
                    Current Cycle
                  </button>
                );
              })()}

              {hasActiveFilters && (
                <div className="flex-1 min-w-0">
                  <ActiveFilters
                    filters={filters}
                    metadata={metadata ?? null}
                    onRemoveFilter={removeFilter}
                    onClearAll={clearFilters}
                  />
                </div>
              )}
            </div>

            {/* Right Side: Sort Controls, Import, and Export */}
            <div className="flex items-center gap-3">
              <SortControls
                sortBy={filters.sortBy}
                sortOrder={filters.sortOrder}
                onSortChange={(sortBy, sortOrder) => updateFilters({ sortBy, sortOrder })}
              />

              {/* Import Dropdown Menu */}
              {canCreateOKR() && (
                <div className="relative">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowImportMenu(!showImportMenu)}
                    className="whitespace-nowrap"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Import
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>

                  {showImportMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowImportMenu(false)}
                      />
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-stone-200 z-20">
                        <button
                          onClick={() => {
                            setImportType('objectives');
                            setIsImportModalOpen(true);
                            setShowImportMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 first:rounded-t-lg"
                        >
                          Import Objectives
                        </button>
                        <button
                          onClick={() => {
                            setImportType('key-results');
                            setIsImportModalOpen(true);
                            setShowImportMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 last:rounded-b-lg border-t border-stone-100"
                        >
                          Import Key Results
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              <Button
                variant="secondary"
                size="sm"
                icon={Download}
                onClick={() => setIsExportModalOpen(true)}
                className="whitespace-nowrap"
              >
                Export
              </Button>
            </div>
          </div>
        </Card>

        {/* OKRs List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <Card className="p-8 text-center">
            <p className="text-red-600 mb-4">Failed to load OKRs</p>
            <Button variant="secondary" onClick={() => refetch()}>
              Try Again
            </Button>
          </Card>
        ) : data && data.okrs.length > 0 ? (
          <div className="space-y-4">
            {data.okrs.map((okr) => (
              <Card
                key={okr.id}
                className={`p-5 hover:shadow-soft-md transition-all cursor-pointer border-l-4 ${
                  okr.status === 'on_track' ? 'border-l-success-500' :
                  okr.status === 'at_risk' ? 'border-l-warning-500' :
                  okr.status === 'off_track' ? 'border-l-danger-500' : 'border-l-stone-300'
                }`}
                onClick={() => openOKR(okr.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-stone-900">{okr.title}</h3>
                      <Badge variant={getTypeBadgeVariant(okr.type)}>{okr.type}</Badge>
                      <Badge variant={getStatusBadgeVariant(okr.status)}>
                        {okrStatusLabel(okr.status)}
                      </Badge>
                      {okr.cycle && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-600">
                          <Calendar className="w-3 h-3" />
                          {okr.cycle.name}
                        </span>
                      )}
                    </div>

                    {okr.description && (
                      <p className="text-stone-500 mb-3 line-clamp-2">{okr.description}</p>
                    )}

                    <div className="flex items-center gap-4 text-sm text-stone-500">
                      <span>
                        Owner: {okr.owner.firstName} {okr.owner.lastName}
                      </span>
                      <span>•</span>
                      <span>{okr.keyResults.length} Key Results</span>
                      <span>•</span>
                      <span className="inline-flex items-center gap-1">
                        <MessageSquare className="w-4 h-4" />
                        {okr._count?.comments || 0}
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-stone-600">Progress</span>
                        <span className="text-sm font-semibold text-stone-900">{okr.progress || 0}%</span>
                      </div>
                      <div className="w-full bg-stone-100 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            (okr.progress || 0) >= 70
                              ? 'bg-success-500'
                              : (okr.progress || 0) >= 40
                              ? 'bg-warning-500'
                              : 'bg-danger-500'
                          }`}
                          style={{ width: `${okr.progress || 0}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => openOKR(okr.id)}
                    >
                      View
                    </Button>
                    {canCreateOKR() && (
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={Copy}
                        onClick={() => handleDuplicate(okr)}
                        title="Duplicate this OKR"
                      >
                        Copy
                      </Button>
                    )}
                    {canDeleteOKR() && (user?.id === okr.ownerId || user?.role === 'account_admin' || user?.id === okr.owner?.managerId) && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(okr)}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}

            {/* Pagination */}
            {data.totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                {Array.from({ length: data.totalPages }, (_, i) => i + 1).map((page) => (
                  <Button
                    key={page}
                    variant={page === data.page ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={() => updateFilters({ page })}
                  >
                    {page}
                  </Button>
                ))}
              </div>
            )}
          </div>
        ) : (() => {
          // Period-aware empty state: when the only active filter is the auto-applied
          // current cycle, show "No OKRs for <cycle name>" with a "View all cycles" CTA.
          const activeCycle = metadata?.cycles?.find((c: any) => c.isActive);
          const onlyCycleFilter =
            !!activeCycle &&
            (filters.cycleIds?.length === 1) &&
            filters.cycleIds[0] === activeCycle.id &&
            !filters.search &&
            !(filters.types && filters.types.length > 0) &&
            !(filters.statuses && filters.statuses.length > 0) &&
            !(filters.departmentIds && filters.departmentIds.length > 0) &&
            filters.progressMin === undefined &&
            filters.progressMax === undefined &&
            !filters.startDate &&
            !filters.endDate;

          if (onlyCycleFilter) {
            return (
              <div>
                <EmptyState
                  title={`No OKRs for ${activeCycle.name}`}
                  description="No OKRs have been defined for this period yet."
                  action={
                    canCreateOKR()
                      ? { label: 'Create OKR', onClick: () => openWithReturn('/okrs/new') }
                      : { label: 'View all cycles', onClick: () => updateFilters({ cycleIds: [] }) }
                  }
                />
                {canCreateOKR() && (
                  <div className="text-center mt-3">
                    <Button variant="ghost" size="sm" onClick={() => updateFilters({ cycleIds: [] })}>
                      View all cycles
                    </Button>
                  </div>
                )}
              </div>
            );
          }

          return (
            <EmptyState
              title="No OKRs found"
              description={
                hasActiveFilters
                  ? 'No OKRs match your current filters. Try adjusting your filter criteria.'
                  : canCreateOKR()
                    ? 'Get started by creating your first Objective and Key Results.'
                    : 'No OKRs are available for you to view.'
              }
              action={
                hasActiveFilters
                  ? { label: 'Clear Filters', onClick: clearFilters }
                  : canCreateOKR()
                    ? { label: 'Create Your First OKR', onClick: () => openWithReturn('/okrs/new') }
                    : undefined
              }
            />
          );
        })()}
      </div>

      {/* Filter Panel */}
      <FilterPanel
        isOpen={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        filters={filters}
        onFiltersChange={updateFilters}
        onApply={() => refetch()}
        onClear={clearFilters}
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        defaultType="objectives"
        availableTypes={['objectives', 'key_results', 'both']}
        title="Export OKRs"
      />

      {/* Import Modal */}
      <ImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        type={importType}
        onImportComplete={() => {
          refetch();
          setIsImportModalOpen(false);
        }}
      />
    </div>
  );
};
